import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CaretCircleDoubleDown,
  CaretCircleDown,
  CaretCircleRight,
  CaretCircleUp,
  CaretCircleDoubleUp,
  Virus,
  House,
  PawPrint,
  CurrencyDollar,
  ExclamationMark,
  Clock,
} from '@phosphor-icons/react';
import { useAuth } from '../hooks/useAuth';
import { useVisitAllocation } from '../hooks/useVisitAllocation';
import CustomerCard from '../components/CustomerCard';
import './ServiceDetails.css';

const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const weekdayIndexes = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };

const getLocalDateString = (date, timeZone) => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date).map(({ type, value }) => [type, value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
};

const getStartDateOptions = (weekday, startTime, timeZone, count = 12) => {
  const targetDay = weekdayIndexes[weekday];
  if (targetDay === undefined || !/^([01]\d|2[0-3]):[0-5]\d$/.test(startTime || '')) return [];

  const today = getLocalDateString(new Date(), timeZone);
  const todayValue = new Date(`${today}T00:00:00Z`);
  const offset = (targetDay - todayValue.getUTCDay() + 7) % 7;
  const firstDate = new Date(todayValue);
  firstDate.setUTCDate(firstDate.getUTCDate() + offset);
  const localNowTime = new Intl.DateTimeFormat('en-GB', {
    timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).format(new Date());
  if (offset === 0 && startTime <= localNowTime) firstDate.setUTCDate(firstDate.getUTCDate() + 7);

  return Array.from({ length: count }, (_, index) => {
    const date = new Date(firstDate);
    date.setUTCDate(firstDate.getUTCDate() + index * 7);
    return date.toISOString().slice(0, 10);
  });
};

const severityConfig = {
  1: { label: 'Very Low', className: 'risk-very-low', icon: CaretCircleDoubleDown },
  2: { label: 'Low', className: 'risk-low', icon: CaretCircleDown },
  3: { label: 'Medium', className: 'risk-medium', icon: CaretCircleRight },
  4: { label: 'High', className: 'risk-high', icon: CaretCircleUp },
  5: { label: 'Critical', className: 'risk-critical', icon: CaretCircleDoubleUp },
};

const categoryConfig = {
  Infection: { icon: Virus },
  Home: { icon: House },
  Pet: { icon: PawPrint },
  Funding: { icon: CurrencyDollar },
  Other: { icon: ExclamationMark },
  General: { icon: ExclamationMark },
  Environment: { icon: House },
  Allergy: { icon: ExclamationMark },
};

const EditService = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeUserEmail, activeUserRoles, activeEmployeeId, loading: authLoading } = useAuth();
  const { workers, workersError, canAssign } = useVisitAllocation(() => {});
  const roleHeader = (activeUserRoles || []).join(',');
  const [service, setService] = useState(null);
  const [saving, setSaving] = useState(false);
  const [recurrenceSaving, setRecurrenceSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [recurrenceError, setRecurrenceError] = useState(null);
  const [recurrenceSuccess, setRecurrenceSuccess] = useState('');
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [recurrenceConfirmationOpen, setRecurrenceConfirmationOpen] = useState(false);
  const confirmationDialog = useRef(null);
  const recurrenceDialog = useRef(null);
  const recurrenceRequestInFlight = useRef(false);
  const [loading, setLoading] = useState(true);
  const [expandedRiskIds, setExpandedRiskIds] = useState({});
  const [recurrenceStartDate, setRecurrenceStartDate] = useState('');
  const [formData, setFormData] = useState({
    workerAllocation: '',
    scheduledDay: '',
    startTime: '',
  });

  useEffect(() => {
    const dialog = confirmationDialog.current;
    if (!dialog) return;
    if (confirmationOpen && !dialog.open) dialog.showModal();
    if (!confirmationOpen && dialog.open) dialog.close();
  }, [confirmationOpen]);

  useEffect(() => {
    const dialog = recurrenceDialog.current;
    if (!dialog) return;
    if (recurrenceConfirmationOpen && !dialog.open) dialog.showModal();
    if (!recurrenceConfirmationOpen && dialog.open) dialog.close();
  }, [recurrenceConfirmationOpen]);

  useEffect(() => {
    if (authLoading) return;

    setConfirmationOpen(false);
    setSaveError(null);
    let isMounted = true;
    const loadServiceData = async () => {
      try {
        setLoading(true);
        const res = await fetch(`/api/services/${encodeURIComponent(id)}?employeeId=${encodeURIComponent(activeEmployeeId || '167')}`, {
          headers: {
            'x-kcs-user-email': activeUserEmail || '',
            'x-kcs-user-roles': roleHeader,
            'x-kcs-provider-employee-id': activeEmployeeId || '167',
          },
        });

        if (!res.ok) {
          throw new Error('Service not found');
        }

        const data = await res.json();
        if (isMounted) {
          const cust = typeof data.customer === 'object' && data.customer !== null
            ? data.customer
            : {
                displayName: data.customer || data.displayName || 'Unknown Customer',
                program: data.program || 'Support at Home',
                riskProfile: data.risk || data.riskProfile || 'Low',
              };

          const firstFutureVisit = data.firstFutureVisit || {};
          const startTime = firstFutureVisit.startTime || '';
          const worker = data.defaultWorkerId || '';

          const normalizedService = {
            id: String(data.serviceId || data.id || id),
            serviceName: data.name || data.serviceType || 'Care Service',
            serviceType: data.serviceType || data.rawServiceName || data.name || 'Care Service',
            customer: cust,
            customerId: String(data.customerId || cust.id || ''),
            schedule: data.frequency || data.program || 'Ongoing',
            duration: data.duration || '45 mins',
            worker,
            defaultWorker: data.defaultWorker || null,
            timeZone: firstFutureVisit.timeZone || 'Australia/Sydney',
            risks: Array.isArray(data.risks) ? data.risks : [],
          };

          setService(normalizedService);
          setFormData({
            workerAllocation: worker,
            scheduledDay: firstFutureVisit.scheduledDay || '',
            startTime,
          });
          setRecurrenceStartDate(firstFutureVisit.scheduledDate || '');
        }
      } catch (err) {
        console.error('Error loading service:', err);
        if (isMounted) {
          setService(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadServiceData();

    return () => {
      isMounted = false;
    };
  }, [id, authLoading, activeEmployeeId, activeUserEmail, roleHeader]);

  const recurrenceStartOptions = getStartDateOptions(
    formData.scheduledDay,
    formData.startTime,
    service?.timeZone || 'Australia/Sydney'
  );

  useEffect(() => {
    const options = getStartDateOptions(formData.scheduledDay, formData.startTime, service?.timeZone || 'Australia/Sydney');
    setRecurrenceStartDate((current) => options.includes(current) ? current : (options[0] || ''));
  }, [formData.scheduledDay, formData.startTime, service?.timeZone]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'workerAllocation') setConfirmationOpen(false);
    if (name === 'scheduledDay' || name === 'startTime') {
      setRecurrenceError(null);
      setRecurrenceSuccess('');
      setRecurrenceConfirmationOpen(false);
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleReplaceRecurrence = async () => {
    if (!canAssign || recurrenceSaving || recurrenceRequestInFlight.current || !recurrenceStartDate) return;
    recurrenceRequestInFlight.current = true;
    setRecurrenceError(null);
    setRecurrenceSuccess('');
    setRecurrenceSaving(true);
    try {
      const url = `/api/services/${encodeURIComponent(service.id)}/recurrence?employeeId=${encodeURIComponent(activeEmployeeId || '167')}`;
      const body = {
        scheduledDay: formData.scheduledDay,
        startDate: recurrenceStartDate,
        startTime: formData.startTime,
      };
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-kcs-user-email': activeUserEmail || '',
          'x-kcs-user-roles': roleHeader,
          'x-kcs-provider-employee-id': activeEmployeeId || '167',
        },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      if (!response.ok) {
        const error = new Error(result.error || 'Unable to replace the service recurrence.');
        error.status = response.status;
        throw error;
      }
      setRecurrenceConfirmationOpen(false);
      setRecurrenceSuccess(`New recurrence created from ${recurrenceStartDate} at ${formData.startTime}.`);
    } catch (error) {
      setRecurrenceError(error.message);
      if (error.status === 409) setRecurrenceConfirmationOpen(false);
    } finally {
      recurrenceRequestInFlight.current = false;
      setRecurrenceSaving(false);
    }
  };

  const getRisksArray = (data) => {
    if (!data) return [];
    if (Array.isArray(data.risks)) return data.risks;
    if (Array.isArray(data.relevantRisks)) return data.relevantRisks;
    if (data.customer && Array.isArray(data.customer.risks)) return data.customer.risks;
    if (data.customer && Array.isArray(data.customer.relevantRisks)) return data.customer.relevantRisks;
    return [];
  };

  const rawRisks = getRisksArray(service);
  const visibleRisks = rawRisks.filter((r) => {
    if (!r) return false;
    if (typeof r === 'string') return true;
    return r.category !== 'Funding' && r.category !== 'Funder';
  });

  const toggleRiskExpand = (riskId) => {
    setExpandedRiskIds(prev => ({
      ...prev,
      [riskId]: !prev[riskId],
    }));
  };

  const workerWarning = service && formData.workerAllocation !== service.worker;

  const handleSave = async () => {
    setSaveError(null);
    if (!canAssign || saving || !workerWarning || !confirmationOpen) return;
    setSaving(true);
    try {
      const response = await fetch(`/api/services/${encodeURIComponent(service.id)}/default-worker?employeeId=${encodeURIComponent(activeEmployeeId || '167')}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-kcs-user-email': activeUserEmail || '',
          'x-kcs-user-roles': roleHeader,
          'x-kcs-provider-employee-id': activeEmployeeId || '167',
        },
        body: JSON.stringify({ workerId: formData.workerAllocation || null, confirmOverride: true }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Unable to save the default worker.');
      navigate('/provider/services');
    } catch (error) {
      setSaveError(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!service) {
    return <div className="error">Service not found</div>;
  }

  return (
    <div className="service-details">
      <div className="page-header">
        <button onClick={() => navigate(-1)} className="back-button">
          ← Back
        </button>
        <h1>Edit Service</h1>
      </div>

      <div className="details-grid">
        <CustomerCard customer={service.customer} />

        <div className="info-card">
          <h2 className="card-title">Service Details</h2>
          <div className="info-row">
            <span className="label">Type:</span>
            <strong className="value">{service.serviceType}</strong>
          </div>
          <div className="info-row">
            <span className="label">Frequency:</span>
            <span className="value">{service.schedule}</span>
          </div>
          <div className="info-row">
            <span className="label">Duration:</span>
            <span className="value">{service.duration}</span>
          </div>
          <div className="info-row info-row-editable">
            <label className="label" htmlFor="defaultWorker">Default Worker:</label>
            <select
              id="defaultWorker"
              name="workerAllocation"
              value={formData.workerAllocation}
              onChange={handleInputChange}
              className="form-select card-inline-select"
              disabled={!canAssign || saving || Boolean(workersError)}
            >
              <option value="">- Unassigned -</option>
              {service.worker && !workers.some((worker) => worker.id === service.worker) && (
                <option value={service.worker} disabled>{service.defaultWorker || 'Unavailable user'}</option>
              )}
              {(workers || []).map((w) => {
                const name = `${w.firstName} ${w.lastName}`;
                return (
                  <option key={w.id} value={w.id}>
                    {name}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="info-row">
            <span className="label">Instructions:</span>
            <span className="value">Mop the floors, clean the kitchen, vacuum the stairs.</span>
          </div>
          <div className="service-card-actions">
            <button
              type="button"
              onClick={() => navigate(`/provider/schedule?service=${encodeURIComponent(service.id)}&customer=${encodeURIComponent(service.customer.displayName)}&clientId=${encodeURIComponent(service.customerId)}&limit=30`)}
              className="service-calendar-button"
              title="View this service recurrence in Schedule"
            >
              <Clock size={32} weight="regular" />
              <span>View recurrence in Schedule</span>
            </button>
          </div>
        </div>

        <div className="info-card risks-card">
          <h2 className="card-title">Risks</h2>
          {visibleRisks.length === 0 ? (
            <p className="no-risks">No active risks</p>
          ) : (
            <ul className="risk-list">
              {visibleRisks.map((risk, index) => {
                const riskId = typeof risk === 'object' && risk.id ? risk.id : `risk-${index}`;
                const category = typeof risk === 'object' && risk.category ? risk.category : 'General';
                const severityVal = typeof risk === 'object' && typeof risk.severity === 'number' ? risk.severity : 3;
                const severity = severityConfig[severityVal] || severityConfig[3];
                const SeverityIcon = severity.icon;
                const CategoryIcon = categoryConfig[category]?.icon || ExclamationMark;
                const expanded = !!expandedRiskIds[riskId];
                const riskText = typeof risk === 'string' ? risk : (risk.risk || risk.description || risk.name || 'Risk noted');

                return (
                  <li key={riskId} className="risk-item-row">
                    <div className="risk-main-content">
                      <div className="risk-header-row">
                        <div className="risk-tag-group">
                          <span className="risk-category-pill">
                            <CategoryIcon size={16} weight="fill" />
                            {category}
                          </span>
                          <span className={`severity-pill ${severity.className}`}>
                            <SeverityIcon size={18} weight="fill" />
                            {severity.label}
                          </span>
                        </div>
                      </div>
                      <p className={`risk-description ${expanded ? 'expanded' : 'collapsed'}`}>
                        {riskText}
                      </p>
                    </div>
                    {(riskText || '').length > 140 && (
                      <button
                        type="button"
                        className={`risk-toggle ${expanded ? 'expanded' : ''}`}
                        onClick={() => toggleRiskExpand(riskId)}
                        aria-label={expanded ? 'Collapse risk' : 'Expand risk'}
                      >
                        <span>›</span>
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

      </div>

      <div className="validation-form">
        <h2 className="form-title">Service Recurrence</h2>
        {workersError && <p role="alert">{workersError}</p>}
        {saveError && !confirmationOpen && <p role="alert">{saveError}</p>}

        <div className="form-row recurrence-row">
          <div className="form-group">
            <label className="form-label">Day of the week</label>
            <select
              name="scheduledDay"
              disabled={!canAssign || recurrenceSaving}
              value={formData.scheduledDay}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="">No future visit</option>
              {weekdays.map((weekday) => <option key={weekday} value={weekday}>{weekday}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Start time</label>
            <input
              type="time"
              name="startTime"
              disabled={!canAssign || recurrenceSaving}
              value={formData.startTime}
              onChange={handleInputChange}
              className="timestamp-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="recurrenceStartDate">New start date and time</label>
            <select
              id="recurrenceStartDate"
              value={recurrenceStartDate}
              disabled={!canAssign || recurrenceSaving || recurrenceStartOptions.length === 0}
              onChange={(event) => {
                setRecurrenceError(null);
                setRecurrenceSuccess('');
                setRecurrenceStartDate(event.target.value);
              }}
              className="form-select"
            >
              {recurrenceStartOptions.map((date) => (
                <option key={date} value={date}>
                  {new Date(`${date}T12:00:00Z`).toLocaleDateString('en-AU', {
                    timeZone: 'UTC', weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
                  })} {formData.startTime}
                </option>
              ))}
            </select>
          </div>
        </div>

        {recurrenceError && <p role="alert">{recurrenceError}</p>}
        {recurrenceSuccess && <p role="status">{recurrenceSuccess}</p>}

        <div className="form-actions">
          <button type="button" onClick={() => navigate(-1)} className="btn-secondary">
            Cancel
          </button>
          <button type="button" onClick={() => { setSaveError(null); setConfirmationOpen(true); }} className="btn-primary" disabled={!canAssign || saving || !workerWarning || Boolean(workersError)}>
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button type="button" className="btn-primary" onClick={() => setRecurrenceConfirmationOpen(true)} disabled={!canAssign || recurrenceSaving || !formData.scheduledDay || !formData.startTime || !recurrenceStartDate || Boolean(workersError) || Boolean(recurrenceSuccess)}>
            {recurrenceSuccess ? 'Replacement complete' : 'Replace recurrence'}
          </button>
        </div>
      </div>
      <dialog
        ref={recurrenceDialog}
        className="service-default-confirmation"
        aria-labelledby="replace-recurrence-title"
        aria-describedby="replace-recurrence-description"
        aria-busy={recurrenceSaving}
        onCancel={(event) => { if (recurrenceSaving) event.preventDefault(); else setRecurrenceConfirmationOpen(false); }}
        onClose={() => setRecurrenceConfirmationOpen(false)}
      >
        <h2 id="replace-recurrence-title">Replace this recurrence?</h2>
        <p id="replace-recurrence-description">
          The existing recurrence will end the day before {recurrenceStartDate}. A copy will start on {recurrenceStartDate} at {formData.startTime}, repeating on {formData.scheduledDay}. Conflicts will be checked before changes are made.
        </p>
        {recurrenceSaving && <p role="status">Updating recurrence. Please wait; this may take several seconds.</p>}
        {recurrenceError && <p role="alert">{recurrenceError}</p>}
        <div className="service-default-confirmation-actions">
          <button type="button" className="btn-secondary" autoFocus disabled={recurrenceSaving} onClick={() => setRecurrenceConfirmationOpen(false)}>Cancel</button>
          <button type="button" className="btn-primary" disabled={recurrenceSaving} onClick={handleReplaceRecurrence}>
            {recurrenceSaving ? 'Replacing...' : 'Confirm replacement'}
          </button>
        </div>
      </dialog>
      <dialog
        ref={confirmationDialog}
        className="service-default-confirmation"
        aria-labelledby="service-default-confirmation-title"
        aria-describedby="service-default-confirmation-description"
        onCancel={(event) => { if (saving) event.preventDefault(); else setConfirmationOpen(false); }}
        onClose={() => setConfirmationOpen(false)}
      >
        <h2 id="service-default-confirmation-title">Change default worker?</h2>
        <p id="service-default-confirmation-description">
          {formData.workerAllocation
            ? 'ALL future jobs for this service will change to the selected worker.'
            : 'ALL future jobs for this service will become unassigned.'}
          {' '}This overrides existing allocations, including individually changed visits. Past visits will not change.
        </p>
        {saveError && <p role="alert">{saveError}</p>}
        <div className="service-default-confirmation-actions">
          <button type="button" className="btn-secondary" autoFocus disabled={saving} onClick={() => setConfirmationOpen(false)}>Cancel</button>
          <button type="button" className="btn-primary" disabled={saving} onClick={handleSave}>{saving ? 'Saving...' : 'Confirm change'}</button>
        </div>
      </dialog>
    </div>
  );
};

export default EditService;
